import { mergeWith, toPairs } from 'lodash'

import { Colors, ServicesNestJSWithNewOrdering, joinURLPath } from '@sigma-frontend-libs/commons'
import { Macro } from '@sigma-legacy-libs/cache'

import store from '@/store'
import { getSocket, request } from '@/utils'

import Service from './Service'

const notificationTypeMap = {
  created: Colors.success,
  updated: Colors.info,
  removed: Colors.warning
}

const mergeWithReplacingArrays = (a, b) => {
  if (Array.isArray(b)) {
    return b
  }

  return undefined
}

export const paramsMapByGeneration = {
  legacy: {
    search: '$search',
    order: '$order',
    limit: '$limit',
    offset: '$offset'
  },
  nest: {
    search: 'search',
    order: 'order',
    limit: 'limit',
    offset: 'offset'
  }
}

export const sanitizeEmptyStringFields = target => {
  if (!target || typeof target !== 'object') {
    return target
  }

  Object.keys(target).forEach(key => {
    const value = target[key]
    if (value === '') {
      target[key] = undefined

      return
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      sanitizeEmptyStringFields(value)
    }
  })

  return target
}

const getAbortKey = function(restMethod) {
  let contextId = 'global'
  if (this.ctx && this.ctx._uid) {
    contextId = this.ctx._uid
  }

  return [ contextId, this.nameViaPoint, restMethod ].join(':')
}

const defaultServiceOptions = {
  cacher: new Macro({
    ttl: 5 * 1000,
    ttlInterval: 1000
  }),

  get: {
    async method(id, params = {}) {
      return await request.get(joinURLPath(this.path, id), { abortKey: getAbortKey.call(this, 'get') }, params.query)
    },

    abort() {
      request.abort('GET', getAbortKey.call(this, 'get'))
    },

    paramsSanitizer(params = {}) {
      return mergeWith({}, this.options.get.params, params, mergeWithReplacingArrays)
    }
  },

  find: {
    async method(params = {}) {
      return await request.find(this.path, { abortKey: getAbortKey.call(this, 'find') }, params.query)
    },

    abort() {
      request.abort('GET', getAbortKey.call(this, 'find'))
    },

    paramsSanitizer(params = {}) {
      const query = {}
      query[paramsMapByGeneration[this.options.backendGeneration].offset] = this.findData.pagination.offset
      query[paramsMapByGeneration[this.options.backendGeneration].limit] = this.findData.pagination.limit

      if (this.findData.filter && typeof this.findData.filter === 'object') {
        Object.assign(query, this.findData.filter)
      }

      const filter = { query }
      if (Object.values(ServicesNestJSWithNewOrdering).includes(this.options.name)) {
        filter.query.orderField = this.findData.order.orderField
        filter.query.orderDirection = this.findData.order.orderDirection
      } else {
        filter.query[paramsMapByGeneration[this.options.backendGeneration].order] = toPairs(this.findData.order)
      }

      sanitizeEmptyStringFields(filter.query)

      return mergeWith(
        filter,
        this.options.find.params,
        params,
        mergeWithReplacingArrays
      )
    }
  },

  update: {
    async method(id, data, params = {}) {
      return await request.update(joinURLPath(this.path, id), {
        abortKey: getAbortKey.call(this, 'update'),
        body: data
      }, params.query)
    },

    abort() {
      request.abort('PUT', getAbortKey.call(this, 'update'))
    },

    paramsSanitizer(params = {}) {
      return mergeWith({}, this.options.update.params, params, mergeWithReplacingArrays)
    }
  },

  patch: {
    async method(id, data, params = {}) {
      return await request.patch(joinURLPath(this.path, id), {
        abortKey: getAbortKey.call(this, 'patch'),
        body: data
      }, params.query)
    },

    abort() {
      request.abort('PATCH', getAbortKey.call(this, 'patch'))
    },

    paramsSanitizer(params = {}) {
      return mergeWith({}, this.options.patch.params, params, mergeWithReplacingArrays)
    }
  },

  create: {
    async method(data, params = {}) {
      return await request.create(this.path, {
        abortKey: getAbortKey.call(this, 'create'),
        body: data
      }, params.query)
    },

    abort() {
      request.abort('POST', getAbortKey.call(this, 'create'))
    },

    paramsSanitizer(params = {}) {
      return mergeWith({}, this.options.create.params, params, mergeWithReplacingArrays)
    }
  },

  remove: {
    async method(id, params = {}) {
      return await request.remove(joinURLPath(this.path, id), { abortKey: getAbortKey.call(this, 'remove') }, params.query)
    },

    abort() {
      request.abort('DELETE', getAbortKey.call(this, 'remove'))
    },

    paramsSanitizer(params = {}) {
      return mergeWith({}, this.options.remove.params, params, mergeWithReplacingArrays)
    }
  }
}

export const generateServices = (services = []) => {
  if (!Array.isArray(services)) {
    services = [ services ]
  }

  const mixin = {
    data() {
      const restData = {}
      for (const [ serviceName, service ] of Object.entries(this.rest)) {
        restData[serviceName] = {
          get: service.getData,
          find: service.findData,
          create: service.createData,
          update: service.updateData,
          remove: service.removeData
        }
      }

      return { restData }
    },

    beforeCreate() {
      this.rest = {}
      this.unwatchers = []

      for (let service of services) {
        const options = mergeWith(
          { context: this },
          defaultServiceOptions,
          service,
          mergeWithReplacingArrays
        )
        service = new Service(options)

        this.rest[service.as] = service
      }
    },

    created() {
      for (const serviceName in this.rest) {
        const service = this.rest[serviceName]
        service.init(this)

        if (Array.isArray(service.websocketEvents)) {
          for (const { event, handler } of service.websocketEvents) {
            const sockets = getSocket()
            if (!sockets) {
              continue
            }
            const wrapper = data => {
              return handler.call(service, data)
            }
            service.socketEventHandlers.push({
              event,
              wrapper
            })
            sockets.on(event, wrapper)
          }
        }

        if (Array.isArray(service.watchers)) {
          service.watchers.forEach(watcher => {
            this.unwatchers.push(this.$watch(...watcher))
          })
        }
      }
    },

    beforeDestroy() {
      for (const serviceName in this.rest) {
        const service = this.rest[serviceName]
        const socket = getSocket()
        if (socket && Array.isArray(service.socketEventHandlers)) {
          for (const { event, wrapper } of service.socketEventHandlers) {
            socket.off(event, wrapper)
          }
        }
      }

      for (const unwatcher of this.unwatchers) {
        unwatcher()
      }
    },

    methods: {
      restGet(serviceName, id, params = {}) {
        return this.rest[serviceName].get(id, params)
      },
      restFind(serviceName, params = {}) {
        return this.rest[serviceName].find(params)
      },
      restUpdate(serviceName, id, data = {}, params = {}) {
        return this.rest[serviceName].update(id, data, params)
      },
      restPatch(serviceName, id, data = {}, params = {}) {
        return this.rest[serviceName].patch(id, data, params)
      },
      restCreate(serviceName, data = {}, params = {}) {
        return this.rest[serviceName].create(data, params)
      },
      restRemove(serviceName, id, params = {}) {
        return this.rest[serviceName].remove(id, params)
      }
    }
  }

  return mixin
}

export const serviceNotify = (serviceName, method, ctx) => {
  const type = notificationTypeMap[method]
  if (!type) {
    return
  }
  let text = `${serviceName} ${method}`
  if (ctx && ctx.getTranslate) {
    text = ctx.getTranslate(`${serviceName}.snackbars.${method}`)
  }

  store.dispatch(
    'notifications/addSnackbar',
    {
      text,
      type
    },
    { root: true }
  )
}

export * from './Service'
export * from './webSocketsEvents'

